import {
  DialogPopupTypes,
  noImageAvatar,
  SelectOption,
  Constraints
} from '@touche-lts/panel-ui/dist'
import { makeAutoObservable } from 'mobx'
import { StoreDTO, UserDTO, UserRoleDTO, UserRoleName } from 'models'
import { Api } from 'services'
// import { RootStore } from 'mobx-stores/root-store'
import moment from 'moment'
import { validate } from 'validate.js'
import { userDtoConstraints } from './user-profile-page-store.utils'

export class UserProfilePageStore {
  private userApi: Api['userApi']
  private storeApi: Api['storeApi']
  private rootStore: any

  constructor (rootStore: any) {
    this.userApi = rootStore.generalStore.api.userApi
    this.storeApi = rootStore.generalStore.api.storeApi
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  // OBSERVABLES
  public user: UserDTO = null
  public editMode: boolean = false
  public validationErrors: Constraints<UserDTO> = {}
  public fullName: string = ''
  public assignedStores: StoreDTO[] = []

  // ACTIONS
  private setUser = (user: UserDTO): void => {
    this.user = user
  }

  private setEditMode = (value: boolean): void => {
    this.editMode = value
  }

  public setFullName = (value: string): void => {
    this.fullName = value
  }

  private setValidationErrors = (errors: Constraints<UserDTO>): void => {
    this.validationErrors = errors
  }

  private setAssignedStores = (stores: StoreDTO[]): void => {
    this.assignedStores = stores
  }

  // METHODS
  public clearStore = (): void => {
    this.setUser(null)
    this.setEditMode(false)
    this.setFullName('')
    this.setValidationErrors({})
    this.setAssignedStores([])
  }

  public loadUser = async (userId: string | number): Promise<void> => {
    const user = await this.userApi.getUser(userId)
    if (!user) return
    this.setUser(user)
    const assignedStores = await this.storeApi.getStoresByUser(
      user.emailAddress
    )
    if (!assignedStores) return
    this.setAssignedStores(assignedStores)
  }

  public onEditButtonClick = (): void => {
    this.rootStore.generalStore.loadUserRoles()
    this.setFullName(this.userFullName)
    this.setEditMode(true)
  }

  public setUserProps = (props: Partial<UserDTO>): void => {
    this.setUser({ ...this.user, ...props })
  }

  private handleFullName = (): void => {
    const [firstName, ...rest] = this.fullName.trim().split(' ')
    const lastName = rest.join(' ').trim() || ''

    this.setUserProps({ firstName, lastName })
  }

  public onDeleteUserClick = (): void => {}

  public onDiscardChangesClick = (): void => {
    const {
      generalStore: { openDialogPopup, closeDialogPopup }
    } = this.rootStore

    openDialogPopup({
      type: DialogPopupTypes.WARNING,
      buttons: [
        {
          children: 'Proceed',
          preset: 'primary',
          onClick: () => {
            const userId = this.user?.id
            userId && this.loadUser(userId)
            this.clearStore()
            closeDialogPopup()
          }
        }
      ],
      closeButtonLabel: 'Cancel',
      message: 'Changes you have made will be lost!'
    })
  }

  public onSubmit = async (): Promise<void> => {
    this.handleFullName()
    const validationErrors = validate(this.user, userDtoConstraints)
    if (validationErrors) {
      this.setValidationErrors(validationErrors)
      return
    }

    const updatedUserDto = await this.userApi.saveUser(this.user)
    if (!updatedUserDto) return
    this.clearStore()
    this.setUser(updatedUserDto)
  }

  // public onAssignStoreClick = async ({ id }: StoreDTO): Promise<void> => {
  //   if (this.hasAssignedStores) {
  //     this.rootStore.generalStore.showError(
  //       'Assigning more stores to this user is not available at the moment.'
  //     )
  //     return
  //   }
  //   const store = await this.storeApi.assignUser(id, this.user)
  //   if (!store) return
  //   this.setAssignedStores([...this.assignedStores, store])
  // }

  public onAssignStoreClick = async ({ id }: StoreDTO): Promise<void> => {
    const store = await this.storeApi.assignUser(id, this.user)
    if (!store) return
    this.setAssignedStores([...this.assignedStores, store])
  }
  


  public onUnassignStoreClick = async (storeId: number): Promise<void> => {
    const store = await this.storeApi.removeUser(
      storeId,
      this.user.emailAddress
    )
    if (!store) return
    this.setAssignedStores(this.assignedStores.filter(s => s.id !== storeId))
  }

  // COMPUTED
  public get userFullName (): string {
    if (!this.user) return ''

    const { firstName, lastName } = this.user
    return `${firstName || ''} ${lastName || ''}`.trim()
  }

  public get userImageUrl (): string {
    if (!this.user) return noImageAvatar
    const { imageUrl } = this.user
    return imageUrl || noImageAvatar
  }

  public get userAddedOn (): string {
    if (!this.user) return ''
    const { created } = this.user

    return moment(created).format('DD.MM.YYYY')
  }

  public get userRolesLabel (): string {
    return this.user?.role?.label || ''
  }

  public get userRoleSelectOptions (): SelectOption<UserRoleDTO>[] {
    return this.rootStore.generalStore.userRoleSelectOptions.filter(
      role => role.value.name !== UserRoleName.VIEWER
    )
  }

  public get selectedUserRole (): UserRoleDTO {
    const selectedRoleId = this.user?.role?.id
    if (!selectedRoleId) return '' as any
    return (
      this.userRoleSelectOptions.find(role => role.value.id === selectedRoleId)
        ?.value || ('' as any)
    )
  }

  public get hasAssignedStores (): boolean {
    return Boolean(this.assignedStores?.length)
  }
}
